import { backdropContainer, backdropImage } from 'assets/jss/material-dashboard-pro-react';
import { useAdCampaignDetail } from 'data/ad-campaign';
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { EAdCampaignStatus } from 'types/ad-campaign';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';

import { ADS_DASHBOARD_ROUTE, useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import MarketingProfileIncompleteModal from 'components/CampaignSteps/MarketingProfileIncompleteModal';
import DashboardBubble from 'components/DashboardBubble/DashboardBubble';
import DashboardSection from 'components/DashboardSection/DashboardSection';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import { updateAdCampaign } from 'utils/api/ad-campaigns';
import { showAPIErrorAlert } from 'utils/lib';

import AdCampaignGeneralTab from './AdCampaignGeneralTab';
import AdCampaignLeadsTab from './AdCampaignLeadsTab';
import AdCampaignSettingsTab from './AdCampaignSettingsTab';
import { toast } from 'sonner';

const useStyles = makeStyles((theme) => ({
  backdropContainer,
  backdropImage,
  root: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 400,

    '& h4': {
      fontWeight: 'bold',
      marginBottom: '4px',
      fontSize: '14px',
      lineHeight: '18px',
      color: theme.palette.black.main,
    },
  },

  instructionsBubble: {
    margin: '47px auto 32px',

    '& > button': { // "Click here"
      font: 'inherit',
      verticalAlign: 'top', // Line up with text
      textDecoration: 'underline',
      color: theme.palette.orange.main,

      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
  },

  infoContainer: {
    marginTop: '20px',
    '& > div': {
      marginBottom: '30px',
    },
  },

  adminInfo: {
    padding: '32px 16px',
    fontWeight: 400,
    fontSize: '14px',
  },

  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
  linkClicks: {
    display: 'flex',
    alignItems: 'center',
    '& a': {
      marginLeft: '5px',
      color: theme.palette.orange.main,
      textDecoration: 'underline',
    },
  },
}));

const tabs = [
  {
    name: 'General',
    slug: '',
  },
  {
    name: 'Leads',
    slug: 'leads',
  },
  {
    name: 'Settings',
    slug: 'settings',
  },
];

// Fallback to touches tab as the default in either case
const getSlugForTab = (s: string | undefined) => tabs.find(({ name }) => name === s)?.slug ?? '';
const getTabForSlug = (s: string | undefined) => tabs.find(({ slug }) => slug === s)?.name ?? 'General';

interface RouteParams {
  adCampaignId: string;
  slug?: string;
}

export default () => {
  const { adCampaignId, slug } = useParams<RouteParams>();

  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { isAnyAgent } = useAuthDataContext();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any; // TODO: TEMP AS ANY

  const [activeTab, setActiveTab] = React.useState(getTabForSlug(slug));

  React.useEffect(() => {
    const route = `/dashboard/ad-campaigns/${adCampaignId}/${getSlugForTab(activeTab)}`;
    if (!slug) {
      history.replace(route); // Replace since no slug existed
    } else {
      history.push(route);
    }
  }, [activeTab]);

  if (!adCampaignId) {
    // Invalid url params. Redirect to dashboard
    history.replace(ADS_DASHBOARD_ROUTE);
  }

  const {
    adCampaign, isLoadingAdCampaign, isErrorAdCampaign, mutateAdCampaign,
  } = useAdCampaignDetail(adCampaignId);

  const [showMarketingProfileIncompleteModal, setShowMarketingProfileIncompleteModal] = React.useState(false);

  React.useEffect(() => {
    if (isErrorAdCampaign) {
      // Likely caused by an invalid campaign id in the url params. Redirect to dashboard
      history.push(ADS_DASHBOARD_ROUTE);
      console.error(isErrorAdCampaign);
    }
  }, [isErrorAdCampaign]);

  React.useEffect(() => {
    if (!adCampaign) return;

    setShowMarketingProfileIncompleteModal(adCampaign.status === EAdCampaignStatus.PendingMarketingProfile);
  }, [adCampaign]);

  const onUpdateAdCampaignName = async (name: string) => {
    showLoadingIndicatorModal();
    try {
      await updateAdCampaign(adCampaignId, { name });

      mutateAdCampaign({
        data: {
          ...adCampaign,
          name,
        },
      }, { revalidate: false });

      toast.success('Ad name updated');
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    }

    hideLoadingIndicatorModal();
  };

  if (isLoadingAdCampaign) {
    return (
      <div className={classes.root}>
        <LoadingIndicator />
      </div>
    );
  }

  let tabContent;

  if (activeTab === 'General') {
    tabContent = (<AdCampaignGeneralTab adCampaignId={adCampaignId} />);
  } else if (activeTab === 'Leads') {
    tabContent = (<AdCampaignLeadsTab adCampaignId={adCampaignId} />);
  } else if (activeTab === 'Settings') {
    tabContent = (<AdCampaignSettingsTab adCampaignId={adCampaignId} />);
  }

  let tabsToShow = tabs.map(({ name }) => name);

  // Don't show the leads tab for digital ads without custom call to action links
  if (adCampaign.useCustomCallToActionLink) {
    tabsToShow = tabsToShow.filter((e) => e !== 'Leads');
  }

  // Don't show the settings tab for cancelled digital ads and non agents
  if (!isAnyAgent || ![EAdCampaignStatus.PendingMarketingProfile, EAdCampaignStatus.Active, EAdCampaignStatus.Paused].includes(adCampaign.status)) {
    tabsToShow = tabsToShow.filter((e) => e !== 'Settings');
  }

  return (
    <div className={classes.root}>
      <DashboardSection
        breadcrumbs={[{ name: 'Digital Ads', to: (location.state as any)?.previousPathname ?? ADS_DASHBOARD_ROUTE }]}
        sectionName={adCampaign.name}
        sectionNameEditable={isAnyAgent}
        onSectionEditName={onUpdateAdCampaignName}
        tabs={tabsToShow}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        buttonLayout="right"
      />

      {adCampaign.status === EAdCampaignStatus.PendingBilling && (
        <DashboardBubble
          rootClass={classes.instructionsBubble}
          backgroundColor={theme.palette.primary.light}
          icon={<InfoIcon htmlColor={theme.palette.secondary.main} />}
          content="We are awaiting payment before starting your Digital Ad."
        />
      )}

      {/* For pending marketing profile state */}
      {showMarketingProfileIncompleteModal && (
        <MarketingProfileIncompleteModal
          onClose={() => setShowMarketingProfileIncompleteModal(false)}
          message="You must complete your marketing profile before we can start showing your digital ad to your target audience!"
        />
      )}

      {adCampaign.status === EAdCampaignStatus.PendingAdApproval && (
        <DashboardBubble
          rootClass={classes.instructionsBubble}
          backgroundColor={theme.palette.primary.light}
          icon={<InfoIcon htmlColor={theme.palette.secondary.main} />}
          content="Your ad is pending review by our Ad Networks and will start delivering to your target audience once approved!"
        />
      )}

      {adCampaign.status === EAdCampaignStatus.Paused && (
        <DashboardBubble
          rootClass={classes.instructionsBubble}
          backgroundColor={theme.palette.primary.light}
          icon={<InfoIcon htmlColor={theme.palette.secondary.main} />}
          content="This ad has been paused. Resume it to start showing your ad to your target audience!"
        />
      )}

      {tabContent}
    </div>
  );
};
